import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Inbox, Check, X, CalendarClock, ArrowLeftRight, UserPlus, Eye } from "lucide-react";

const mondayOf = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x.toISOString().slice(0, 10); };
const KIND = { unavailable: "Μη διαθέσιμος", available: "Διαθέσιμος", preferred: "Προτίμηση" };
const fmtShift = (s) => s ? `${(s.date || "").slice(5)} ${(s.start || "").slice(0, 5)}–${(s.end || "").slice(0, 5)}${s.role_name ? " · " + s.role_name : ""}` : "—";

export default function ScheduleRequests() {
  const qc = useQueryClient();
  const [week, setWeek] = useState(mondayOf(new Date()));
  const { data: claims = [] } = useQuery({ queryKey: ["tt-claims"], queryFn: async () => (await api.get("/team/claims?status=pending")).data });
  const { data: trades = [] } = useQuery({ queryKey: ["tt-trades"], queryFn: async () => (await api.get("/team/trades?status=pending")).data });
  const { data: avail = [] } = useQuery({ queryKey: ["tt-avail-pending"], queryFn: async () => (await api.get("/team/availability?status=pending")).data });
  const { data: receipts } = useQuery({ queryKey: ["tt-receipts", week], queryFn: async () => (await api.get(`/team/schedule/receipts?week_start=${week}`)).data });

  const refetch = () => { qc.invalidateQueries({ queryKey: ["tt-claims"] }); qc.invalidateQueries({ queryKey: ["tt-trades"] }); qc.invalidateQueries({ queryKey: ["tt-avail-pending"] }); qc.invalidateQueries({ queryKey: ["tt-receipts"] }); };

  const resolveClaim = useMutation({ mutationFn: async ({ id, status }) => (await api.put(`/team/claims/${id}`, { status })).data, onSuccess: () => { toast.success("Ενημερώθηκε"); refetch(); }, onError: (e) => toast.error(apiErr(e)) });
  const resolveTrade = useMutation({ mutationFn: async ({ id, status }) => (await api.put(`/team/trades/${id}`, { status })).data, onSuccess: () => { toast.success("Ενημερώθηκε"); refetch(); }, onError: (e) => toast.error(apiErr(e)) });
  const resolveAvail = useMutation({ mutationFn: async ({ id, status }) => (await api.put(`/team/availability/${id}/approve`, { status })).data, onSuccess: () => { toast.success("Ενημερώθηκε"); refetch(); }, onError: (e) => toast.error(apiErr(e)) });

  const Approve = ({ onY, onN, testidY, testidN }) => (
    <div className="flex gap-2 shrink-0">
      <button data-testid={testidY} onClick={onY} className="h-9 w-9 rounded-lg bg-[#E2ECE4] text-[#3F6B4C] flex items-center justify-center hover:bg-[#d4e3d7]"><Check className="h-4 w-4" /></button>
      <button data-testid={testidN} onClick={onN} className="h-9 w-9 rounded-lg bg-[#F6E2DD] text-[#A1493A] flex items-center justify-center hover:bg-[#f0d4cc]"><X className="h-4 w-4" /></button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="schedule-requests-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Team &amp; Time</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1 inline-flex items-center gap-2"><Inbox className="h-7 w-7" /> Αιτήματα Προγράμματος</h1>
      </div>

      <section data-testid="claims-section">
        <h2 className="font-serif text-xl text-[#45423C] mb-3 inline-flex items-center gap-2"><UserPlus className="h-5 w-5" /> Διεκδικήσεις ανοιχτών βαρδιών ({claims.length})</h2>
        <div className="space-y-2">
          {claims.map((c) => (
            <div key={c.id} data-testid={`claim-${c.id}`} className="rounded-xl bg-white border border-[#E3DED4] p-4 flex items-center justify-between gap-3">
              <div><span className="font-medium text-[#45423C]">{c.member_name}</span><div className="text-xs text-[#8A8577]">{fmtShift(c.shift)}</div></div>
              <Approve testidY={`claim-approve-${c.id}`} testidN={`claim-reject-${c.id}`} onY={() => resolveClaim.mutate({ id: c.id, status: "approved" })} onN={() => resolveClaim.mutate({ id: c.id, status: "rejected" })} />
            </div>
          ))}
          {claims.length === 0 && <p className="text-sm text-[#6F6A60]">Καμία εκκρεμότητα.</p>}
        </div>
      </section>

      <section data-testid="trades-section">
        <h2 className="font-serif text-xl text-[#45423C] mb-3 inline-flex items-center gap-2"><ArrowLeftRight className="h-5 w-5" /> Ανταλλαγές & Κάλυψη ({trades.length})</h2>
        <div className="space-y-2">
          {trades.map((t) => (
            <div key={t.id} data-testid={`trade-${t.id}`} className="rounded-xl bg-white border border-[#E3DED4] p-4 flex items-center justify-between gap-3">
              <div>
                <span className="font-medium text-[#45423C]">{t.from_member_name} → {t.to_member_name}</span>
                <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-[#F1EDE6] text-[#6F6A60]">{t.type === "swap" ? "Ανταλλαγή" : t.type === "cover" ? "Κάλυψη" : "Παράδοση"}</span>
                <div className="text-xs text-[#8A8577]">{fmtShift(t.shift)}</div>
              </div>
              <Approve testidY={`trade-approve-${t.id}`} testidN={`trade-reject-${t.id}`} onY={() => resolveTrade.mutate({ id: t.id, status: "approved" })} onN={() => resolveTrade.mutate({ id: t.id, status: "rejected" })} />
            </div>
          ))}
          {trades.length === 0 && <p className="text-sm text-[#6F6A60]">Καμία εκκρεμότητα.</p>}
        </div>
      </section>

      <section data-testid="availability-section">
        <h2 className="font-serif text-xl text-[#45423C] mb-3 inline-flex items-center gap-2"><CalendarClock className="h-5 w-5" /> Αιτήματα διαθεσιμότητας ({avail.length})</h2>
        <div className="space-y-2">
          {avail.map((a) => (
            <div key={a.id} data-testid={`avail-${a.id}`} className="rounded-xl bg-white border border-[#E3DED4] p-4 flex items-center justify-between gap-3">
              <div>
                <span className="font-medium text-[#45423C]">{a.member_name}</span>
                <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-[#F3EADF] text-[#9A6A3A]">{KIND[a.kind] || a.kind}</span>
                <div className="text-xs text-[#8A8577]">{a.recurring ? `Κάθε ${["Δευ", "Τρί", "Τετ", "Πέμ", "Παρ", "Σάβ", "Κυρ"][a.weekday] || "?"}` : a.date}{a.start ? ` · ${a.start}–${a.end}` : " · όλη μέρα"}{a.note ? ` · ${a.note}` : ""}</div>
              </div>
              <Approve testidY={`avail-approve-${a.id}`} testidN={`avail-reject-${a.id}`} onY={() => resolveAvail.mutate({ id: a.id, status: "approved" })} onN={() => resolveAvail.mutate({ id: a.id, status: "rejected" })} />
            </div>
          ))}
          {avail.length === 0 && <p className="text-sm text-[#6F6A60]">Καμία εκκρεμότητα.</p>}
        </div>
      </section>

      <section data-testid="receipts-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-xl text-[#45423C] inline-flex items-center gap-2"><Eye className="h-5 w-5" /> Επιβεβαιώσεις ανάγνωσης{receipts ? ` (${receipts.read}/${receipts.total})` : ""}</h2>
          <input data-testid="receipts-week" type="date" value={week} onChange={(e) => setWeek(mondayOf(e.target.value))} className="rounded-xl border border-[#E3DED4] px-3 py-2 text-sm" />
        </div>
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {(receipts?.rows || []).map((rr) => (
            <div key={rr.member_id} className="flex items-center justify-between text-sm">
              <span className="text-[#45423C]">{rr.member_name}</span>
              {rr.read_at ? <span className="text-[11px] text-[#3F6B4C] inline-flex items-center gap-1"><Check className="h-3 w-3" /> είδε</span> : <span className="text-[11px] text-[#B9B0A2]">εκκρεμεί</span>}
            </div>
          ))}
          {(receipts?.rows || []).length === 0 && <p className="text-sm text-[#6F6A60]">Καμία δημοσιευμένη βάρδια αυτή την εβδομάδα.</p>}
        </div>
      </section>
    </div>
  );
}
